import { useCallback, useEffect, useMemo, useState } from 'react';
import { useToast } from '../components/ui';
import { normaliseText } from './helpers';

// Headless CRUD-list logic shared by the per-page mobile/desktop UI files.
// This replaces the generic CrudModule for pages that now ship a dedicated
// folder (events / functions / guests): the LOGIC lives here, each platform
// renders its own JSX. Same shape as the old CrudModule props.
//
// Params:
//   load     () => Promise<rows[]>
//   onSave   (values, editingRow|null) => Promise   (caller does Add/Update)
//   onDelete (row) => Promise
//   fields   [{ key, type, live, default, required, label }]  (for form init + validation)
//   can      { add, edit, delete }
//   confirmDelete  optional (row) => string  (window.confirm message; default generic)
//
// Returns a `vm` object the .desktop.jsx / .mobile.jsx files consume.
export function useCrudList({ load, onSave, onDelete, fields, can = {}, confirmDelete }) {
  const toast = useToast();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [values, setValues] = useState({});
  const [saving, setSaving] = useState(false);
  const [q, setQ] = useState('');

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const data = await load();
      setRows(Array.isArray(data) ? data : []);
    } catch {
      toast('Failed to load');
    } finally {
      setLoading(false);
    }
  }, [load, toast]);

  useEffect(() => { refresh(); }, [refresh]);

  const startAdd = () => {
    const init = {};
    fields.forEach(f => { init[f.key] = f.default ?? ''; });
    setValues(init); setEditing(null); setOpen(true);
  };
  const startEdit = (row) => {
    const init = {};
    fields.forEach(f => { init[f.key] = row[f.key] ?? ''; });
    setValues(init); setEditing(row); setOpen(true);
  };
  const closeForm = () => setOpen(false);

  // Back-key support: reset all fields to their defaults (keeps the form open).
  const clearForm = () => {
    const init = {};
    fields.forEach(f => { init[f.key] = f.default ?? ''; });
    setValues(init);
  };
  // Dirty = any field differs from its default value (so a freshly-opened or
  // just-cleared add form reads as empty and the next back press closes it).
  const isDirty = () => fields.some(f => {
    const def = f.default ?? '';
    return String(values[f.key] ?? '') !== String(def);
  });

  const setField = (f, v) => {
    const nv = f.live ? normaliseText(v) : v;
    setValues(prev => ({ ...prev, [f.key]: nv }));
  };

  const save = async () => {
    for (const f of fields) {
      if (f.required && !String(values[f.key] ?? '').trim()) {
        toast(`${f.label} is required`); return;
      }
    }
    setSaving(true);
    try {
      await onSave(values, editing);
      toast(editing ? 'Updated' : 'Added');
      setOpen(false);
      refresh();
    } catch (err) {
      toast(err?.response?.data?.error || err?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const del = async (row) => {
    const msg = confirmDelete ? confirmDelete(row) : 'Delete this record?';
    if (!window.confirm(msg)) return;
    try {
      await onDelete(row);
      toast('Deleted');
      refresh();
    } catch (err) {
      toast(err?.response?.data?.error || err?.message || 'Delete failed');
    }
  };

  const filtered = useMemo(() => (
    q ? rows.filter(r => JSON.stringify(r).toLowerCase().includes(q.toLowerCase())) : rows
  ), [rows, q]);

  return {
    rows, filtered, loading, q, setQ,
    open, editing, values, saving, fields, can,
    startAdd, startEdit, closeForm, clearForm, isDirty, setField, save, del, refresh,
  };
}
